import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { CreditCard, Building2, Save, Info, ExternalLink, Shield, Loader2, TrendingUp, Download, Zap } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSettingsPage() {
  const { token } = useAuth();
  const [settings, setSettings] = useState({ iban: "", bic: "", account_holder: "", has_own_payment: false, payment_url: "", commission_rate: 5 });
  const [stats, setStats] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchSettings(); fetchStats(); fetchStripe(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API}/api/payment/settings`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setSettings(await res.json());
    } catch {} finally { setLoading(false); }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/api/payment/stats`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setStats(await res.json());
    } catch {}
  };

  const fetchStripe = async () => {
    try {
      const res = await fetch(`${API}/api/stripe/connect/status`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setStripeStatus(await res.json());
    } catch {}
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/api/payment/export.csv`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "paiements_arcanapp.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Erreur export"); } finally { setExporting(false); }
  };

  const connectStripe = async () => {
    setConnecting(true);
    try {
      const res = await fetch(`${API}/api/stripe/connect/onboard`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ return_url: window.location.href, refresh_url: window.location.href }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else toast.error(data.detail || "Erreur Stripe");
    } catch { toast.error("Erreur réseau"); } finally { setConnecting(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/payment/settings`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(settings),
      });
      if (res.ok) toast.success("Parametres sauvegardes");
      else toast.error("Erreur");
    } catch { toast.error("Erreur reseau"); }
    setSaving(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#FF6B35]" /></div>;

  return (
    <div className="space-y-6" data-testid="payment-settings-page">
      <div className="flex items-center gap-3">
        <CreditCard className="w-7 h-7 text-[#FF6B35]" />
        <h1 className="text-2xl font-bold text-white">Mes informations financières</h1>
      </div>

      {/* Payment statistics */}
      <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-5 space-y-4" data-testid="payment-stats">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#FF6B35]" />
            <h2 className="text-lg font-semibold text-white">Statistiques de paiement</h2>
          </div>
          <button onClick={exportCsv} disabled={exporting} data-testid="export-csv-btn"
            className="flex items-center gap-2 px-3 py-2 bg-[#252525] hover:bg-[#2e2e2e] text-white rounded-lg text-sm transition-colors disabled:opacity-50">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Export CSV (Stripe)
          </button>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-[#121212] rounded-lg p-3 border border-[#252525]">
            <p className="text-[#666] text-xs">Total encaissé</p>
            <p className="text-white text-xl font-bold" data-testid="stat-revenue">{stats ? stats.total_revenue.toFixed(2) : "0.00"} €</p>
          </div>
          <div className="bg-[#121212] rounded-lg p-3 border border-[#252525]">
            <p className="text-[#666] text-xs">Inscriptions payées</p>
            <p className="text-white text-xl font-bold" data-testid="stat-paid">{stats ? stats.paid_count : 0}</p>
          </div>
          <div className="bg-[#121212] rounded-lg p-3 border border-[#252525]">
            <p className="text-[#666] text-xs">Commission Arcanapp ({stats ? stats.commission_rate : settings.commission_rate}%)</p>
            <p className="text-amber-400 text-xl font-bold">{stats ? stats.commission_amount.toFixed(2) : "0.00"} €</p>
          </div>
          <div className="bg-[#121212] rounded-lg p-3 border border-[#252525]">
            <p className="text-[#666] text-xs">Net estimé</p>
            <p className="text-green-400 text-xl font-bold">{stats ? stats.net_revenue.toFixed(2) : "0.00"} €</p>
          </div>
        </div>
        {stats?.recent?.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-[#666] text-left border-b border-[#252525]">
                <th className="py-2">Date</th><th>Tournoi</th><th>Email</th><th className="text-right">Montant</th><th className="text-right">Statut</th>
              </tr></thead>
              <tbody>
                {stats.recent.slice(0, 8).map((r, i) => (
                  <tr key={i} className="border-b border-[#1f1f1f] text-[#aaa]">
                    <td className="py-1.5">{r.date ? new Date(r.date).toLocaleDateString("fr-FR") : "-"}</td>
                    <td className="truncate max-w-[160px]">{r.tournament}</td>
                    <td className="truncate max-w-[160px]">{r.email}</td>
                    <td className="text-right text-white">{r.amount.toFixed(2)} €</td>
                    <td className="text-right">{r.status === "paid" ? <span className="text-green-400">Payé</span> : <span className="text-[#666]">{r.status}</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Stripe Connect */}
      <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-5 space-y-3" data-testid="stripe-section">
        <div className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-[#635BFF]" />
          <h2 className="text-lg font-semibold text-white">Compte Stripe</h2>
        </div>
        <p className="text-xs text-[#888]">Reliez votre compte Stripe pour recevoir les paiements des inscriptions directement.</p>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm">
            {stripeStatus?.charges_enabled ? (
              <span className="flex items-center gap-1.5 text-green-400"><span className="w-2 h-2 rounded-full bg-green-400" />Compte Stripe actif</span>
            ) : stripeStatus?.connected ? (
              <span className="flex items-center gap-1.5 text-amber-400"><span className="w-2 h-2 rounded-full bg-amber-400" />Onboarding Stripe à finaliser</span>
            ) : (
              <span className="flex items-center gap-1.5 text-[#666]"><span className="w-2 h-2 rounded-full bg-[#666]" />Aucun compte Stripe relié</span>
            )}
          </div>
          <button onClick={connectStripe} disabled={connecting} data-testid="connect-stripe-btn"
            className="flex items-center gap-2 px-4 py-2 bg-[#635BFF] hover:bg-[#5249e0] text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
            {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            {stripeStatus?.charges_enabled ? "Gérer Stripe" : stripeStatus?.connected ? "Finaliser Stripe" : "Connecter Stripe"}
          </button>
        </div>
      </div>

      <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-5">
        <div className="flex items-start gap-3 text-sm">
          <Info className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
          <div className="text-[#aaa] space-y-2">
            <p><strong className="text-white">Comment fonctionne le paiement ?</strong></p>
            <p>Les inscriptions payantes sur <strong className="text-[#FF6B35]">l'application Arcanapp</strong> passent par notre tunnel de paiement securise. Une commission de <strong className="text-white">{settings.commission_rate}%</strong> est appliquee par Arcanapp pour les frais de gestion.</p>
            <p>Si vous avez votre propre tunnel de paiement (Stripe, PayPal, etc.), vous pouvez l'utiliser pour les inscriptions via <strong className="text-[#FF6B35]">votre widget iframe</strong>. Dans ce cas, les paiements vous sont verses directement, sans commission Arcanapp.</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="w-5 h-5 text-[#FF6B35]" />
            <h2 className="text-lg font-semibold text-white">Coordonnees bancaires</h2>
          </div>
          <p className="text-xs text-[#888]">Pour recevoir les paiements des inscriptions via l'app Arcanapp.</p>

          <div>
            <label className="text-sm text-[#888] mb-1 block">Titulaire du compte</label>
            <input value={settings.account_holder} onChange={(e) => setSettings({ ...settings, account_holder: e.target.value })}
              placeholder="Nom de la boutique ou association"
              className="w-full bg-[#121212] border border-[#333] rounded-lg px-3 py-2 text-white text-sm focus:border-[#FF6B35] outline-none"
              data-testid="account-holder-input" />
          </div>
          <div>
            <label className="text-sm text-[#888] mb-1 block">IBAN</label>
            <input value={settings.iban} onChange={(e) => setSettings({ ...settings, iban: e.target.value.toUpperCase() })}
              placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
              className="w-full bg-[#121212] border border-[#333] rounded-lg px-3 py-2 text-white text-sm focus:border-[#FF6B35] outline-none font-mono"
              data-testid="iban-input" />
          </div>
          <div>
            <label className="text-sm text-[#888] mb-1 block">BIC / SWIFT</label>
            <input value={settings.bic} onChange={(e) => setSettings({ ...settings, bic: e.target.value.toUpperCase() })}
              placeholder="BNPAFRPPXXX"
              className="w-full bg-[#121212] border border-[#333] rounded-lg px-3 py-2 text-white text-sm focus:border-[#FF6B35] outline-none font-mono"
              data-testid="bic-input" />
          </div>

          <div className="pt-2 border-t border-[#252525] flex items-center gap-2 text-xs text-[#666]">
            <Shield className="w-4 h-4" />
            <span>Donnees chiffrees et securisees</span>
          </div>
        </div>

        <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <ExternalLink className="w-5 h-5 text-[#FF6B35]" />
            <h2 className="text-lg font-semibold text-white">Paiement widget iframe</h2>
          </div>
          <p className="text-xs text-[#888]">Configurez votre propre tunnel de paiement pour les inscriptions via votre widget embed.</p>

          <div className="flex items-center gap-3">
            <button onClick={() => setSettings({ ...settings, has_own_payment: !settings.has_own_payment })}
              className={`relative w-12 h-6 rounded-full transition-colors ${settings.has_own_payment ? "bg-[#FF6B35]" : "bg-[#333]"}`}
              data-testid="own-payment-toggle">
              <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${settings.has_own_payment ? "translate-x-6" : "translate-x-0.5"}`} />
            </button>
            <span className="text-sm text-white">J'ai mon propre tunnel de paiement</span>
          </div>

          {settings.has_own_payment && (
            <div>
              <label className="text-sm text-[#888] mb-1 block">URL de votre page de paiement</label>
              <input value={settings.payment_url} onChange={(e) => setSettings({ ...settings, payment_url: e.target.value })}
                placeholder="https://votre-site.com/paiement"
                className="w-full bg-[#121212] border border-[#333] rounded-lg px-3 py-2 text-white text-sm focus:border-[#FF6B35] outline-none"
                data-testid="payment-url-input" />
              <p className="text-xs text-[#666] mt-1">Ce lien sera utilise pour les inscriptions payantes depuis votre widget iframe.</p>
            </div>
          )}

          <div className="bg-[#121212] rounded-lg p-4 border border-[#252525] mt-3">
            <p className="text-sm font-medium text-white mb-2">Recapitulatif</p>
            <div className="space-y-1 text-xs text-[#888]">
              <div className="flex justify-between"><span>Inscriptions via l'app</span><span className="text-white">Tunnel Arcanapp ({settings.commission_rate}% commission)</span></div>
              <div className="flex justify-between"><span>Inscriptions via iframe</span><span className="text-white">{settings.has_own_payment ? "Votre tunnel (0% commission)" : `Tunnel Arcanapp (${settings.commission_rate}% commission)`}</span></div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 bg-[#FF6B35] hover:bg-[#FF6B35]/90 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
          data-testid="save-payment-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Sauvegarder
        </button>
      </div>
    </div>
  );
}
